import { 
  MdLightMode, 
  MdDarkMode,
  MdLanguage,
  MdExpandMore,
  MdPerson,
  MdLogout,
  MdCheck,
  MdMenu
} from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';
import ertinuLogo from '../../assets/images/ertinu_logo.png';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const SuperAdminHeader = ({ isSidebarOpen, onToggleSidebar, isMobile }) => {
  const { t, changeLanguage } = useLanguage();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('language') || 'en');
  const [user, setUser] = useState({
    name: 'Superadmin',
    initials: 'SA'
  });
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    // Dark mode tercihi yükle
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', label: t('common.english') },
    { code: 'tr', label: t('common.turkish') }
  ];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setCurrentLanguage(langCode);
    setIsLanguageOpen(false);
    setIsUserMenuOpen(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superadmin_token');
    localStorage.removeItem('superadmin_user');
    navigate('/superadmin');
  };

  return (
    <header className="h-[90px] bg-[#272c39] text-white border-b border-white/20 shadow-[0_2px_8px_rgba(0,0,0,0.15)] relative z-20">
      <div className="h-full px-3 sm:px-6 flex items-center justify-between">
        <div className="h-full flex items-center gap-2 sm:gap-4">
          {/* Hamburger Menu - Sadece mobilde görünür */}
          {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="p-2 hover:bg-white/10 rounded-md lg:hidden"
              aria-label="Toggle Menu"
            >
              <MdMenu size={24} className="text-white" />
            </button>
          )}
          
          <div className="flex items-center">
            <img 
              src={ertinuLogo} 
              alt="ERTINU Logo" 
              className="h-[35px] object-contain"
            />
          </div>

        </div>
        
        <div className="flex items-center gap-4">
          {/* Theme toggle */}
          <button 
            onClick={toggleDarkMode}
            className="p-1.5 hover:bg-white/10 rounded-full"
          >
            {isDarkMode ? (
              <MdLightMode size={20} className="text-white/90" />
            ) : (
              <MdDarkMode size={20} className="text-white/90" />
            )}
          </button>
          
          {/* Kullanıcı menüsü */}
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 hover:bg-white/10 rounded-md px-2 py-1.5"
            >
              <span className="text-sm text-white/90">{user.name}</span>
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-sm font-medium">{user.initials}</span>
              </div>
            </button>

            {/* Dropdown menü */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 min-w-[200px] border border-gray-200 dark:border-gray-700">
                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <MdLanguage size={18} />
{t('common.language')}
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{currentLanguage}</span>
                      <MdExpandMore 
                        className={`transition-transform duration-200 ${
                          isLanguageOpen ? 'rotate-180' : ''
                        }`}
                        size={18}
                      />
                    </div>
                  </button>

                  {/* Language Dropdown */}
                  {isLanguageOpen && (
                    <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 min-w-[160px] border border-gray-200 dark:border-gray-700">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setIsLanguageOpen(false);
                            setIsUserMenuOpen(false);
                            handleLanguageChange(lang.code);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                        >
                          {lang.label}
                          {currentLanguage === lang.code && (
                            <MdCheck size={18} className="text-blue-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <MdLogout size={18} />
{t('common.logOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </header>
  );
};

export default SuperAdminHeader;
